import { useParams } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import { useEffect, useMemo, useState } from "react";
import { employee_id } from "../../environment";
import useApi from "../../core/customhook/useApi";
import Swal from "sweetalert2";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { Link } from "react-router-dom";

type ClearanceRow = {
    label: string;
    role: string;
    cleared: boolean;
    notCleared: boolean;
    remark: string;
};

const INITIAL_ROWS: ClearanceRow[] = [
    { label: "Manager Clearance", role: "Manager", cleared: false, notCleared: false, remark: "" },
    { label: "Admin Clearance", role: "Administration", cleared: false, notCleared: false, remark: "" },
    { label: "IT Clearance", role: "IT", cleared: false, notCleared: false, remark: "" },
    { label: "F & A Clearance", role: "Finance & Accounts", cleared: false, notCleared: false, remark: "" },
];


const CLEARANCE_SEQUENCE = [
    "Manager",
    "Administration",
    "IT",
    "Finance & Accounts",
];


const ClearanceForm = () => {
    const { employeeId } = useParams<{ employeeId?: string }>();
    const [rows, setRows] = useState<ClearanceRow[]>(INITIAL_ROWS);
    const [count, setCount] = useState<any>(0)
    const [isSubmitted, setIsSubmitted] = useState(false);

    /* ---------------- API CALLS ---------------- */

    const { data: supportteamData } = useApi("/api/get-role", {
        method: "POST",
        payload: { employeeId },
        autoCall: !!employeeId,
    });

    // dynamic url for getting clearance data from both side 
    const url = `/api/get-clearance?employee_id=${employeeId}`

    const {
        data: clearanceData,
        loading,
        callApi: refetchClearance,
    } = useApi(url, {
        method: "GET",
        autoCall: true,
    });

    const { data: clearanceCheck, callApi: checkPreviousClearance } = useApi(
        "/api/clearance/check-previous",
        { method: "POST", autoCall: false }
    );

    const { callApi: submitClearance } = useApi("/api/clearance", {
        method: "POST",
        autoCall: false,
    });

    /* ---------------- LOGGED-IN ROLES ---------------- */

    const loggedInRoles: string[] = useMemo(() => {
        return (
            supportteamData?.data
                ?.filter((item: any) => item.employee_id === employee_id)
                .map((item: any) => item.role) || []
        );
    }, [supportteamData]);

    const canSubmit = loggedInRoles.length > 0;
    console.log("CanSubmit :", canSubmit)

    const canEditRow = (role: string) => {
        if (!loggedInRoles.includes(role)) return false;

        const roleIndex = CLEARANCE_SEQUENCE.indexOf(role);
        if (roleIndex === -1) return false;

        const previousRoles = CLEARANCE_SEQUENCE.slice(0, roleIndex);
        const nextRoles = CLEARANCE_SEQUENCE.slice(roleIndex + 1);


        const previousPending = rows.some(
            (row) =>
                previousRoles.includes(row.role) &&
                !row.cleared
        );


        const nextCleared = rows.some(
            (row) =>
                nextRoles.includes(row.role) &&
                row.cleared
        );

        return !previousPending && !nextCleared;
    };

    // const isRowEditable = (role: string) => {
    //     return loggedInRoles.includes(role);
    // };
    const isRowEditable = (role: string) => {
        return canEditRow(role);
    };

    const hasAlreadySubmitted = useMemo(() => {
        if (!clearanceData?.data || loggedInRoles.length === 0) return false;

        return clearanceData.data.some((item: any) => {
            const isUserRole = loggedInRoles.includes(item.clearance_type);
            const isSubmitted =
                item.is_cleared === 1

            return isUserRole && isSubmitted;
        });
    }, [clearanceData, loggedInRoles]);



    /* ---------------- EFFECTS ---------------- */

    useEffect(() => {
        if (!clearanceData?.data) return;

        setRows((prev) =>
            prev.map((row) => {
                const existing = clearanceData.data.find(
                    (item: any) => item.clearance_type === row.role
                );

                if (!existing) return row;

                return {
                    ...row,
                    cleared: existing.is_cleared === 1,
                    notCleared: existing.is_not_cleared === 1,
                    remark: existing.remark || "",
                };
            })
        );
    }, [clearanceData]);


    // Check previous clearance (first matching role only)
    useEffect(() => {
        if (!employeeId || loggedInRoles.length === 0) return;

        checkPreviousClearance({
            employeeId,
            role: loggedInRoles[0],
        });
    }, [employeeId, loggedInRoles]);

    const handleRemarkChange = (index: number, value: string) => {
        setRows((prev) =>
            prev.map((row, i) => (i === index ? { ...row, remark: value } : row))
        );
    };

    const handleSubmit = async () => {


        if (!canSubmit) {
            Swal.fire("Not Authorized", "You cannot submit clearance.", "warning");
            return;
        }

        if (clearanceCheck && !clearanceCheck.canProceed) {
            Swal.fire(
                "Clearance Pending",
                "Previous department clearance is required.",
                "warning"
            );
            return;
        }

        const selectedRows = rows.filter(
            (row) =>
                (row.cleared || row.notCleared) &&
                loggedInRoles.includes(row.role)
        );


        const clearedRows = selectedRows.filter(row => row.cleared);

        console.log("Role : ", clearedRows)

        if (clearedRows.length > 0) {
            const clearedResult = await Swal.fire({
                title: "Confirm Cleared",
                text: `Are you sure you want to mark this clearance as Cleared?`,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Yes",
                cancelButtonText: "Cancel"
            });
            if (!clearedResult.isConfirmed) return;
        }

        // Confirm Not Cleared rows
        const notClearedRows = selectedRows.filter(row => row.notCleared);
        if (notClearedRows.length > 0) {
            const notClearedResult = await Swal.fire({
                title: "Confirm Not Cleared",
                text: `Are you sure you want to mark this clearance as Not Cleared?`,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Yes",
                cancelButtonText: "Cancel"
            });
            if (!notClearedResult.isConfirmed) return;
        }

        if (selectedRows.length === 0) {
            Swal.fire(
                "Action Required",
                "Please select Cleared or Not Cleared.",
                "warning"
            );
            return;
        }

        const allowedRows = selectedRows.filter((row) =>
            loggedInRoles.includes(row.role)
        );

        if (allowedRows.length === 0) {
            Swal.fire(
                "Unauthorized",
                "You are not allowed to submit these clearances.",
                "error"
            );
            return;
        }

        try {

            await Promise.all(
                allowedRows.map((row) =>
                    submitClearance({
                        employee_id: employeeId,
                        type: row.role,
                        is_cleared: row.cleared ? 1 : 0,
                        is_not_cleared: row.notCleared ? 1 : 0,
                        remark: row.remark || null,
                    })
                )
            );

            Swal.fire("Success", "Clearance submitted successfully.", "success");
            refetchClearance();
        } catch (error) {
            Swal.fire("Error", "One or more submissions failed.", "error");
        }
    };

    /* ---------------- UI ---------------- */

    const handleCheckboxChange = (
        index: number,
        type: "cleared" | "notCleared"
    ) => {
        setRows((prev) =>
            prev.map((row, i) =>
                i === index
                    ? {
                        ...row,
                        cleared: type === "cleared",
                        notCleared: type === "notCleared",
                    }
                    : row
            )
        );
        // setCount(count + 1);
    };

    return (
        <div className="page-wrapper">
            <div className="content">

                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">Work & Operations</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to="/employee-dashboard">
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">Work & Operations</li>
                                <li className="breadcrumb-item">Offboarding</li>
                                <li className="breadcrumb-item active">
                                    <Link to={"#"} aria-current="page">
                                        Team Clearance
                                    </Link>
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                        <div className="head-icons ms-2">
                            <CollapseHeader />
                        </div>
                    </div>
                </div>
                {/* /Breadcrumb */}
                {/*  */}

                <div className="card">
                    <div className="card-header">
                        <h2 className="">Clearance Form</h2>
                    </div>
                    <div className="card-body">
                        <table className="table table-bordered table-striped">
                            <thead className="table-dark text-center">
                                <tr>
                                    <th></th>
                                    <th>Cleared</th>
                                    <th>Not Cleared</th>
                                    <th>Remark</th>
                                </tr>
                            </thead>

                            <tbody>
                                {rows.map((row, index) => (
                                    <tr key={row.role}>
                                        <td className="bg-primary text-white">{row.label}</td>

                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="clearance"
                                                checked={row.cleared}
                                                disabled={!isRowEditable(row.role)}
                                                onChange={() =>
                                                    handleCheckboxChange(index, "cleared")
                                                }
                                            />
                                        </td>

                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="clearance"
                                                checked={row.notCleared}
                                                disabled={!isRowEditable(row.role)}
                                                onChange={() =>
                                                    handleCheckboxChange(index, "notCleared")
                                                }
                                            />
                                        </td>

                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={row.remark}
                                                disabled={!isRowEditable(row.role)}
                                                onChange={(e) =>
                                                    handleRemarkChange(index, e.target.value)
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {canSubmit && !hasAlreadySubmitted && (
                            <div className="text-end mt-3">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    Submit
                                </button>
                            </div>
                        )}

                        {hasAlreadySubmitted && (
                            <div className="alert alert-info text-end mt-3">
                                You have already submitted your clearance.
                            </div>
                        )}

                    </div>
                </div>
            </div>

      <div className="footer text-center border-top p-3">
        © {new Date().getFullYear()} WeConnect. All rights reserved.
      </div>
    </div>
  );
};

export default ClearanceForm;
